import { memo, useCallback, useState } from "react";
import { Popup } from "@/components/popups/Popup";
import { CreateNewCollectionPopup } from "./CreateNewCollectionPopup";
import { useIsAuth } from "@/common/hooks/useIsAuth";
import { CollectionItem } from "./CollectionItem";
import { BaseButton } from "@/components/buttons/BaseButton";
import "./styles/AddToCollectionPopup.scss";
import InfiniteScroll from "react-infinite-scroll-component";
import { useUserCollectionsData } from "@/common/hooks/useUserCollectionsData";
import { ICollection, IErrorData } from "@/common/services/types";
import { EventSource, metricsBus } from "@/common/buses";
import { onCloseAddCollectionPopup } from "@/components/popup-dispatcher/PopupDispatcher";
import { IAddToCollectionPopupProps } from "@/common/buses/popups/popup-types";
import { useUserStore } from "@/common/store/userStore";
import { apiCollections } from "@/common/services";
import { showNotification } from "@/components/alerts/Notifications";
import { AxiosError } from "axios";
import { useAuthNavigate } from "@/common/hooks/useAuthNavigate";
import { useAnalyticsStore } from "@/common/store/analyticsStore";

export const AddToCollectionPopup = memo(({ gifId, position, boosted, playCount }: IAddToCollectionPopupProps) => {
  const userName = useUserStore((state) => state.currentUser.username);
  const [isNewCollectionPopupOpen, setIsOpenNewCollectionPopup] = useState(false);
  const isAuth = useIsAuth();
  const { navigateSignIn } = useAuthNavigate();
  const eventSource = useAnalyticsStore((state) => state.eventSource);
  const sourceId = eventSource?.id ?? null;
  const sourceName = eventSource?.name || EventSource.Other;

  const {
    items,
    isError,
    hasNextPage,
    fetchNextPage
  } = useUserCollectionsData({
    userName,
    isRefetch: isNewCollectionPopupOpen
  });

  const toggleCreateNewCollectionPopup = () => {
    setIsOpenNewCollectionPopup(prev => !prev);
  };

  const addToCollections = async (folderId: string) => {
    try {
      await apiCollections.addGifToCollection({
        collectionId: folderId,
        gifId: gifId,
        context: sourceId,
      });
      showNotification({
        title: "Added to Collection('s)!",
        variant: "success"
      });
      metricsBus.emit("addedToCollection", {
        gifId,
        source: sourceName,
        sourceId,
        position,
        boosted,
        playCount
      });
    } catch (e: unknown) {
      const err = e as AxiosError<IErrorData>;
      const message = err.response?.data?.error?.message ||
        "An error occurred, please try again later";
      showNotification({
        title: message,
        variant: "error"
      });
    }
    handleClose();
  };

  const handleLogin = () => {
    navigateSignIn();
  };

  const handleClose = useCallback(() => {
    onCloseAddCollectionPopup();
  }, []);

  const btnProps = {
    variant: 2,
    title: "Create a new collection",
    onClick: toggleCreateNewCollectionPopup,
    btnType: "submit"
  };

  return (
    <Popup
      title="Add to collection"
      isOpen={true}
      popupClass="AddToCollectionPopup"
      btnProps={btnProps}
      onClose={handleClose}
    >
      {isAuth && (
        <InfiniteScroll
          dataLength={items.length}
          next={fetchNextPage}
          hasMore={!!hasNextPage}
          loader={<></>}
          scrollableTarget="scrollable-container"
        >
          <div id="scrollable-container">
            <ul className="AddToCollectionPopup-List">
              {isError ? (
                <li className="AddToCollectionPopup-NoCollection">
                  We have some issues with loading collections. Please close and open the dialog and try again.
                </li>
              ) : (
                <>
                  {items?.map((collection: ICollection) => (
                    <CollectionItem
                      key={collection.folderId}
                      collection={collection}
                      addToCollections={addToCollections}
                    />
                  ))}
                </>
              )}
            </ul>
          </div>
        </InfiniteScroll>
      )}
      {!isAuth && (
        <div className="AddToCollectionPopup-Login">
          <p>You’ve gotta log in to add to collections to RedGIFs!</p>
          <BaseButton onClick={handleLogin} aria-label="log in">Login</BaseButton>
        </div>
      )}
      {isNewCollectionPopupOpen &&
        <CreateNewCollectionPopup
          isOpen={isNewCollectionPopupOpen}
          togglePopup={toggleCreateNewCollectionPopup}
        />
      }
    </Popup>
  );
});
